import type { ComponentPropsWithRef } from 'react';
import { cn } from '@/lib/utils';
import { Icon as IconUI, Typography } from '@/shared-ui/atoms';
import { PairingArrowIcon } from '@/shared-ui/icons';

type BagCardListVariant = 'default' | 'perfect-pairing';

type BagCardListProps = ComponentPropsWithRef<'div'> & {
    variant?: BagCardListVariant;
};

const Root = ({ className, ref, variant = 'default', children, ...props }: BagCardListProps) => (
    <div
        data-slot="bag-card-list"
        data-variant={variant}
        ref={ref}
        // Default lists are white; the perfect-pairing card is a cream surface (its inner BagCards
        // inherit the cream via a descendant selector in BagCard.Root).
        className={cn(variant === 'perfect-pairing' ? 'bg-snk-cream' : 'bg-snk-white', className)}
        {...props}>
        {variant === 'perfect-pairing' ? (
            // The pairing arrow does NOT overlap the line item above. Instead the card opens with its
            // own white strip (this h-5 band) that provides space for the arrow; the arrow is centred on
            // the strip's bottom edge so it sits fully inside the card (half white strip, half cream).
            // With no margin between the line item above and this card, the white strip reads as a
            // continuation of that line item — the arrow appears to belong to it without covering it.
            <div data-slot="bag-card-list-pairing" className="relative mb-4 flex h-5 w-full items-center">
                <div className="h-full w-full bg-snk-white" />
                <div
                    data-slot="bag-card-list-pairing-icon"
                    aria-hidden
                    className="pointer-events-none absolute left-1/2 top-full -translate-x-1/2 -translate-y-1/2">
                    <IconUI icon={PairingArrowIcon} className="size-10" />
                </div>
            </div>
        ) : null}
        <div className={cn('[&>[data-slot=bag-card]]:pt-0 [&>[data-slot=bag-card]:only-child]:pt-4')}>{children}</div>
    </div>
);
Root.displayName = 'BagCardList.Root';

type HeaderProps = ComponentPropsWithRef<'div'>;

const Header = ({ className, ref, ...props }: HeaderProps) => (
    <div
        data-slot="bag-card-list-header"
        ref={ref}
        className={cn('flex items-center justify-between p-4', className)}
        {...props}
    />
);
Header.displayName = 'BagCardList.Header';

type TitleProps = ComponentPropsWithRef<'h3'>;

const Title = ({ className, ref, children, ...props }: TitleProps) => (
    <Typography
        data-slot="bag-card-list-title"
        variant="h3"
        ref={ref as never}
        className={cn('font-normal mr-4', className)}
        {...props}>
        {children}
    </Typography>
);
Title.displayName = 'BagCardList.Title';

type IconProps = ComponentPropsWithRef<'button'>;

const Icon = ({ className, ref, type = 'button', ...props }: IconProps) => (
    <button
        data-slot="bag-card-list-icon"
        ref={ref}
        type={type}
        className={cn('w-[12.3px] h-[12.3px] inline-flex items-center justify-center', className)}
        {...props}
    />
);
Icon.displayName = 'BagCardList.Icon';

const BagCardList = {
    Root,
    Header,
    Title,
    Icon,
} as const;

export { BagCardList };
